import React, { useRef, useState, useEffect, ReactNode } from 'react';
import ParticleLayer, { ParticleLayerHandle } from './ParticleLayer';
import { ParticleContext } from '../contexts/ParticleContext';
import { useGameSettings } from '../hooks/useGameSettings';

interface ParticleSystemProviderProps {
    children: ReactNode;
}

/**
 * Global provider for the particle effect system.
 * Wrap your <App /> root with this provider to enable particle effects globally.
 * 
 * Components access effects via: const particles = useParticles();
 */
export const ParticleSystemProvider: React.FC<ParticleSystemProviderProps> = ({ children }) => {
    const particleRef = useRef<ParticleLayerHandle | null>(null);
    const [handle, setHandle] = useState<ParticleLayerHandle | null>(null);
    const { settings } = useGameSettings();
    const particlesEnabled = settings.effectsEnabled;

    // Update the context value after the ParticleLayer mounts and the ref is populated
    useEffect(() => {
        // Small delay to ensure ref is populated after forwardRef assignment
        const timer = setTimeout(() => {
            if (particleRef.current && !handle) {
                setHandle(particleRef.current);
            }
        }, 50);
        return () => clearTimeout(timer);
    }, [handle]);

    return (
        <ParticleContext.Provider value={handle}>
            {children}
            {/* ParticleLayer is always rendered for global access.
              Z-index is set high (z-[100]) to ensure effects render above modals (z-50). */}
            <div className="fixed inset-0 pointer-events-none z-[9999]">
                <ParticleLayer ref={particleRef} enabled={particlesEnabled} />
            </div>
        </ParticleContext.Provider>
    );
};

export default ParticleSystemProvider;
